import { useEffect, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { useAtom } from "jotai";
import { imagesAtom } from "../../jotai-store/uploadImagesPersistance";
import {  uploadImagesToNodeApi } from "../../store/actions/visionAgentActions";
import ImageGridArea from "./ImageGridArea";
import LoadingMoveez from "../Lottie/LoadingMoveez";
import { useToaster } from "../../components/Toaster";
import { useRef } from "react";
import { v4 as uuidv4 } from 'uuid';
import { compressImageSmartly } from "../../hooks/compressImage";



const TOTAL_SLOTS = 60; // 3 cols x 5 rows (up to 15 images)



const ImageGalleryModal = ({
  selectedImages = [],
  onClose = () => { },
  isOpen = false,
  onChooseFromGallery = () => { },
  handleTakePhoto = () => { },
  isMobile = false,
  onAddMoreRequest = () => { },
  handleUpdateBookingObject,
  setItems,
  setTripStatus,
  setVisionItemsLoading,
  setBookingTotalModel = () => { },
  setEstimatePricing,
  isProcessingImages = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [stagedDeletes, setStagedDeletes] = useState(new Set());
  const [, setSelectedImages] = useAtom(imagesAtom);
  const [isImagesUploading, setIsImagesUploading] = useState(false);
  const { showToast } = useToaster();
  const controllerRef = useRef(null); // <- Controller store
  const isBusy = isImagesUploading || isProcessingImages;

  // Prepare 12 slots and place selected images in order
  const slots = Array.from({ length: TOTAL_SLOTS }).map((_, i) =>
    selectedImages[i] ? selectedImages[i] : null
  );

  // Count of images that are NOT staged for deletion (used to enable/disable delete-all)
  const unStagedCount = selectedImages.filter(
    (img) => img && !stagedDeletes.has(img.id)
  ).length;


  // Handle animation when isOpen changes
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready for animation
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Reset staged deletes whenever modal opens
  useEffect(() => {
    if (isOpen) setStagedDeletes(new Set());
  }, [isOpen]);

  const toggleStage = (id) => {
    setStagedDeletes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };



  const handleConfirm = async () => {

    // Clear previous vision booking data
    localStorage.removeItem("VBO");
    localStorage.removeItem("VBItems");
    localStorage.removeItem("VBSummary");
    localStorage.removeItem("visionReqId");
    localStorage.removeItem("VBOLog");
    localStorage.removeItem("vision_verif");
    localStorage.removeItem("uploadedFilesMeta");
    localStorage.removeItem("just_registered");
    setBookingTotalModel({});
    setEstimatePricing({});


    // --- STEP 1: FILTER OUT DELETED IMAGES ---
    const remainingImages = selectedImages.filter(
      (img) => !stagedDeletes.has(img.id)
    );

    // If no images remain, just close the modal
    if (remainingImages.length === 0) {
      setSelectedImages([]);
      onClose();
      return;
    }

    try {
      setIsImagesUploading(true);
      setSelectedImages(remainingImages);

      // --- STEP 2: COMPRESS & RENAME IMAGES ---
      const compressionTasks = remainingImages.map(async (img) => {
        if (img.file) {
          const compressedFile = await compressImageSmartly(img.file);

          // Rename file to include unique ID
          const renamedFile = new File(
            [compressedFile],
            `${img.id}___${img.file.name}`,
            { type: compressedFile.type }
          );

          return renamedFile;
        }
        return null;
      });
      // Wait for all compression tasks to complete
      const processedFiles = (await Promise.all(compressionTasks)).filter(Boolean);

      // --- STEP 3: PREPARE FOR UPLOAD ---
      const formData = new FormData();
      processedFiles.forEach((file) => {
        formData.append("files", file);
      });

      controllerRef.current = new AbortController();
      // --- STEP 4: UPLOAD TO NODEJS API ---
      const responseData = await uploadImagesToNodeApi(formData, controllerRef.current.signal);
      console.log("Full Upload Response:", responseData.status);

      if (responseData.status === "success") {



        showToast({
          type: "success",
          message: "Items Analyzed Successfully from Vision Agent!",
        });

        const newItems = responseData?.data?.items?.map((item) => ({
          ...item,
          itemId: uuidv4()
        }));
        const summary = responseData?.data?.summary;


        const existingVBO = JSON.parse(localStorage.getItem("VBO")) || {};

        // --- STEP 5: UPDATE VISION ITEMS IN BOOKING ---
        const updatedVBO = {
          ...existingVBO,
          items: newItems,
          tripStatus: "visionItems",
          updatedAt: new Date().toISOString()
        };

        localStorage.setItem("VBO", JSON.stringify(updatedVBO));
        localStorage.setItem("VBItems", JSON.stringify(newItems));
        localStorage.setItem("VBSummary", JSON.stringify(summary));
        setItems(newItems);
        setTripStatus("visionItems");
        handleUpdateBookingObject(null, updatedVBO, "vision_upload_success");


        onClose();
      } else {
        showToast({
          type: "error",
          message: "Failed to analyze items. Please try again.",
        });
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.log(err);
      } else {
        console.error("Upload Error:", err);
      }
    } finally {
      setIsImagesUploading(false);
      setVisionItemsLoading(false);
      setStagedDeletes(new Set());
    }
  };


  // Cancel button click handler
  const handleCancelUpload = () => {
    if (controllerRef.current) {
      controllerRef.current.abort(); // <- API call cancel ho jayegi
      setIsImagesUploading(false);
      setSelectedImages([]); // Clear selected images on cancel
    }
  };

  // If not open, don't render anything
  if (!isOpen) return null;

  // Previously we returned a full-screen loader; render loader inside the modal instead.

  return (
    <
      div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[#7876A1]/50"
    // onClick={onClose}
    >

      {/* ⬇️ LOADER ALWAYS IN SCREEN CENTER */}
      {isBusy && (
        <div className="fixed inset-0 z-[99999] md:hidden" style={{ pointerEvents: 'none' }}>
          <LoadingMoveez
            isLoadingShow={isBusy}
            text={isProcessingImages ? "Preparing your photos..." : undefined}
          />
        </div>
      )}


      <div
        onClick={(e) => e.stopPropagation()}
        className={`absolute left-0 h-[90%] md:h-[500px] right-0 bottom-0 transform will-change-transform transition-transform duration-300 ease-out w-full bg-white rounded-t-[18px] shadow-xl border border-[#e6eefc] px-[33px] max-380:px-[25px]  flex flex-col justify-between ${isVisible ? "translate-y-0" : "translate-y-full"
          }`}
      >
        <div className="relative h-[65.05px] w-full max-767:max-w-[360px] mx-auto md:w-full md:mx-0">
          <button
            type="button"
            onClick={() => {
              handleCancelUpload();
              onClose();
              setSelectedImages([])
            }}
            className="top-[24px] left-0 absolute"
          >
            <img
              src="/Vision Agent Icons/X Icon/XLightpurple.svg"
              alt="Close"
              className="h-[18.44px] w-[16.5px]"
            />
          </button>
        </div>


        {/* Image grid area */}

        {isBusy ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="hidden md:block">
              <LoadingMoveez
                isLoadingShow={isBusy}
                text={isProcessingImages ? "Preparing your photos..." : undefined}
              />
            </div>
          </div>
        ) : (
          <ImageGridArea
            slots={slots}
            stagedDeletes={stagedDeletes}
            toggleStage={toggleStage}
            onAddMore={onAddMoreRequest}
          />
        )}

        {/* Bottom action bar */}
        {!isBusy && (
          <div className="relative h-24 flex items-center justify-between  md:justify-around w-full max-w-[360px] mx-auto  md:w-96 md:mx-auto mb-[20px] md:mb-[30px]">
            <button
              type="button"
              onClick={onChooseFromGallery}
              className="z-10 invisible"
            >
              <img
                src="/Vision Agent Icons/Camera Roll Icon/Camera Roll - Light purple.svg"
                alt="camera-roll"
              />
            </button>

            <button
              type="button"
              onClick={handleConfirm}
              className="disabled:opacity-50 disabled:cursor-not-allowed  bottom-6 bg-[#CDCCD7] rounded-full w-16 h-16 flex items-center justify-center shadow-md z-20"
              aria-label="confirm"
              disabled={unStagedCount === 0}
            >
              <FaCheck className="text-[var(--primary)] text-xl" />
            </button>

            <button
              type="button"
              disabled={unStagedCount === 0}
              onClick={() => {
                // Keep only images that are staged for deletion; remove the rest
                const kept = selectedImages.filter(
                  (img) => img && stagedDeletes.has(img.id)
                );
                setSelectedImages(kept);
              }}
              className="z-10 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="delete-all"
            >
              <img
                src="/images/NewUiIcons/Trash Can Icon.svg"
                alt="Trash Can Icon"
                width={25}
                height={25}
              />
            </button>

          </div>
        )}
      </div>
    </div>
  );
};

export default ImageGalleryModal;
